/**
 * Runtime configuration for the portal, resolved once at module load.
 *
 * Precedence, highest first:
 *   1. `window.__WEB_CONFIG__` — an optional `/config.js` served next to the
 *      build, so the same static files can be pointed at any backend without
 *      rebuilding (branch previews, new environments). Not part of the bundle.
 *   2. `REACT_APP_*` build-time variables — kept for local development
 *      (`.env.local`) and, until every deployment ships a config.js, for the
 *      per-environment keys that CI still bakes in.
 *   3. Hostname convention — see `./resolve-config.ts`.
 *
 * Import `config` from '@config' rather than reading `process.env` in app code.
 */
import { resolveConfig, type PortalConfig, type PortalConfigOverrides } from './resolve-config';

export type { PortalConfig, PortalConfigOverrides };

// Drop unset values so they don't shadow a lower-priority source. `null`
// matters: vite-plugin-environment inlines optional variables as `null`.
function omitUnset<T extends object>(value: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(value).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ) as Partial<T>;
}

// vite-plugin-environment substitutes these statically, so each variable has
// to be spelled out; `process.env[name]` would not be replaced.
function overridesFromBuildEnv(): PortalConfigOverrides {
    return omitUnset({
        environment: process.env.REACT_APP_ENV,
        graphUrl: process.env.REACT_APP_GRAPH_API_URL,
        kaiserBaseUrl: process.env.REACT_APP_KAISER_API_URL,
        coreServicesUrl: process.env.REACT_APP_CORE_SERVICES_URL,
        splitioApiKey: process.env.REACT_APP_SPLITIO_API_KEY,
        googleMapsApiKey: process.env.REACT_APP_GOOGLE,
        version: process.env.REACT_APP_VERSION,
    });
}

function overridesFromWindow(): PortalConfigOverrides {
    if (typeof window === 'undefined') return {};
    const injected = window.__WEB_CONFIG__;
    return injected && typeof injected === 'object' ? omitUnset(injected) : {};
}

function loadConfig(): PortalConfig {
    const location =
        typeof window !== 'undefined'
            ? new URL(window.location.href)
            : new URL('http://localhost/');
    return resolveConfig(location, {
        ...overridesFromBuildEnv(),
        ...overridesFromWindow(),
    });
}

export const config: PortalConfig = loadConfig();
